import { AfterViewInit, Component, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import {
  DateFieldBuilderComponent
} from '@app/shared/components/fields/date-field-builder/date-field-builder.component';
import { provideDayjsDateAdapter } from '@app/shared/extra/dayjs-date-adapter';
import dayjs, { ConfigType, Dayjs } from 'dayjs';
import { combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, tap } from 'rxjs/operators';

@Component({
  selector: 'date-time-field-builder',
  templateUrl: './date-time-field-builder.component.html',
  styleUrls: ['./date-time-field-builder.component.scss'],
  providers: [provideDayjsDateAdapter()],
  standalone: false
})
export class DateTimeFieldBuilderComponent extends DateFieldBuilderComponent implements OnInit, AfterViewInit {

  /**
   * Initialize form's value and FormStateService, compute form validators.
   */
  public ngOnInit(): void {
    this.form = new FormGroup({
      field: new FormControl(this.initialFormValue, this.computeValidators())
    });
    // Add two specific form controls for the datepicker and timepicker
    this.form.addControl('date', new FormControl<ConfigType | string>(this.initialFormValue, this.computeValidators()));
    this.form.addControl('time', new FormControl<ConfigType | string>(this.initialFormValue, this.computeValidators()));

    // Initialise the field in the FormStateService
    this.setFieldValue(this.formatDate(this.initialFormValue));
    this.setFieldInitialValue(this.formatDate(this.initialFormValue));
    this.getNextState();
  }

  /**
   * Setup validator hooks and listen for user input on the form controls. When a new date or time value is received, propagate
   * to the FormStateService, including validation subject. Prior to propagating, the value from the form field is
   * transformed by {@see formatDate}.
   */
  public ngAfterViewInit(): void {
    this.setupHooks();

    // Util function to narrow the input type to Dayjs instances
    function parseConfig(input: ConfigType | null): Dayjs | null {
      if (!input) return null;
      if (dayjs.isDayjs(input)) return input;
      const parsed = dayjs(input);
      return parsed.isValid() ? parsed : null;
    }

    const dateFormControl = this.form.get('date') as FormControl<ConfigType | string>;
    const timeFormControl = this.form.get('time') as FormControl<ConfigType | string>;

    // Initialise each half of the component and listen when either changes
    combineLatest([
      dateFormControl.valueChanges
        .pipe(
          startWith(dateFormControl.value),
          debounceTime(400),
          distinctUntilChanged(),
          map(it => parseConfig(it))
        ),
      timeFormControl.valueChanges
        .pipe(
          startWith(timeFormControl.value),
          debounceTime(400),
          distinctUntilChanged(),
          map(it => parseConfig(it))
        )
    ]).pipe(
      // Combine both halves of the date-time
      map(([date, time]) => {
        if (!date || !time) return null;

        return dayjs(date)
          .hour(time.hour())
          .minute(time.minute())
          .second(time.second())
          .millisecond(0);
      }),
      // Update the field FormControl, ignore valueChange events
      tap(value => this.form.controls.field.setValue(value)),
      // Format the combined parts into a datetime
      map(value => this.formatDate(value)),
      takeUntilDestroyed(this.destroyRef)
    )
      .subscribe(formattedDate => {
        this.setFieldValue(formattedDate);
        if (this.formStateService.getPath(['AFTER_OTHER_DATE', this.fieldConfig.fieldCode])) {
          this.formStateService.getPath(['AFTER_OTHER_DATE', this.fieldConfig.fieldCode])
            .next(formattedDate);
        }
      });
  }

  /**
   * Cancel edition of this field, reset form value to its initial value.
   */
  public cancel(): void {
    this.form.controls.field.setValue(this.initialFormValue);
    this.form.controls.date.setValue(this.initialFormValue);
    this.form.controls.time.setValue(this.initialFormValue);
    super.cancel();
  }

  /**
   * Format a user input value from the form field as a string represented date-time. Return an empty string if the
   * value is null or not a valid date-time. The value may be any Dayjs-compatible type.
   * @param value Form value.
   * @return String representation of the date-time or empty string.
   * @protected
   */
  protected override formatDate(value?: ConfigType): string {
    return dayjs.isDayjs(value) && value.isValid() ? value.format(this.appConfig.OFFSET_DATETIME_FORMAT) : '';
  }

  /**
   * Format a date value based on the user's locale. Return an empty string if the value is null or not a valid date.
   * @param value Form value. Should be a string representing an ISO date-time.
   * @return Localized string representation of the date-time or empty string.
   * @protected
   */
  protected formatLocalizedDate(value?: ConfigType): string {
    return value && dayjs(value).isValid() ? dayjs(value).format(this.appConfig.DISPLAY_DATETIME_FORMAT) : '';
  }
}
